import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, IconButton, MenuItem, Select, Stack, Typography, useTheme } from "~/common/mui";
import FavoriteOutlinedIcon from "@mui/icons-material/FavoriteOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";

import { ELEVATION_2 } from "~/common/components/icon/Elevation";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import type { MediaItemDetails } from "../../types/mediaLibraryTypes";
import { MEDIA_TYPES, MediaType } from "../../constants";
import { useMediaPreview } from "./useMediaPreview";
import ShareButton from "./ShareButton/ShareButton";

import { lineClamp } from "~/common/components/icon/FontUtils";

const sharedMenuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      boxShadow: ELEVATION_2,
    },
  },
};

export default function MediaPreview({
  id,
  groupId,
  updateMediaItems,
  updateSelectedMediaItem,
  openLoginPopup,
}: {
  id: string;
  groupId: string;
  updateMediaItems: (id: string, updatedFav: boolean) => void;
  updateSelectedMediaItem: (mediaDetails: MediaItemDetails) => void;
  openLoginPopup: () => void;
}) {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();
  const {
    PREVIEWS,
    mediaItem,
    languages,
    selectedLanguage,
    fetchMediaItem,
    handleChangeLanguage,
    handleClickFavourite,
    trackOnShare,
    trackOnShareWith,
  } = useMediaPreview({
    id,
    groupId,
    updateMediaItems,
    updateSelectedMediaItem,
    openLoginPopup,
  });

  useEffect(() => {
    if (id && groupId) {
      fetchMediaItem();
    }
  }, [id, groupId]);

  if (!id || !groupId) {
    return null;
  }

  const theme = useTheme();

  const getActionSection = () => {
    return (
      <Grid
        size={isTablet ? 5 : 12}
        container
        spacing={2}
        justifyContent={!isTablet ? "flex-start" : "flex-end"}
        alignItems="center"
        sx={{ alignItems: "center", ml: "auto" }}
      >
        {languages.length > 1 && (
          <Grid>
            <Select
              labelId="language-simple-select-label"
              id="language-simple-select"
              value={selectedLanguage}
              onChange={handleChangeLanguage}
              size="small"
              MenuProps={sharedMenuProps}
              sx={{ width: "180px", borderRadius: "4px" }}
            >
              {languages.map((item) => (
                <MenuItem value={item} key={item}>
                  {t(`languageName.${item}`)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        )}

        {mediaItem?.downloadUrl && (
          <Grid>
            <ShareButton
              url={mediaItem?.downloadUrl}
              title={mediaItem?.title}
              trackOnShare={trackOnShare}
              trackOnShareWith={trackOnShareWith}
            />
          </Grid>
        )}

        <Grid>
          <IconButton
            variant="contained"
            size="medium"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              handleClickFavourite();
            }}
          >
            {mediaItem?.isFavourite ? <FavoriteOutlinedIcon /> : <FavoriteBorderOutlinedIcon />}
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const InfoItem = ({
    label,
    value,
    size,
  }: {
    label: string;
    value: React.ReactNode;
    size?: { phone: number; tablet: number };
  }) => (
    <Grid size={size || { phone: 6, tablet: 3 }}>
      <Grid container direction="column" spacing={0.5}>
        <Grid>
          <Typography variant="titleSmall" fontWeight={400} color="text.secondary">
            {label}
          </Typography>
        </Grid>
        <Grid>{value}</Grid>
      </Grid>
    </Grid>
  );

  const mediaType = MEDIA_TYPES[mediaItem?.mediaType || "application/x-unknown"] || MediaType.UNKNOWN;
  const isImage = mediaType === MediaType.IMAGE;
  const isPDF = mediaType === MediaType.PDF;

  const previewBoxSx = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 1.25,
    width: 1,
    height: "480px",
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "surface.main",
    mb: 2,
    position: "relative",
    overflow: "hidden",
    ...(isImage && {
      [theme.breakpoints.down("tablet")]: { height: "328px", borderRadius: 0.75 },
    }),
    ...(!isImage && mediaType === MediaType.VIDEO && {
      [theme.breakpoints.down("tablet")]: { height: "170px", borderRadius: 0.75 },
    }),
    ...(!isImage && mediaType !== MediaType.VIDEO && {
      [theme.breakpoints.down("tablet")]: { height: "450px" },
    }),
  };

  return (
    <>
      <Box sx={previewBoxSx}>{mediaItem && PREVIEWS[mediaType].render(mediaItem)}</Box>

      <Grid container rowSpacing={1.5} columnSpacing={3} alignItems="center" paddingBottom={isTablet ? 0 : 1}>
        <Grid size={isTablet ? 7 : 12}>
          <Stack gap={0.5}>
            <Typography variant="titleLarge" component={"b"} color="text.primary">
              {mediaItem?.title}
            </Typography>
            <Typography sx={{ ...lineClamp(1) }} color="text.secondary">
              {mediaItem?.documentType}
            </Typography>
          </Stack>
        </Grid>

        {!isTablet && mediaItem?.description && (
          <Grid size={{ phone: 12 }}>
            <Typography variant="titleMedium" fontWeight={400} color="text.primary">
              {mediaItem?.description}
            </Typography>
          </Grid>
        )}

        <>{getActionSection()}</>

        {isTablet && mediaItem?.description && (
          <Grid size={{ phone: 12 }}>
            <Typography variant="titleMedium" fontWeight={400} color="text.primary">
              {mediaItem?.description}
            </Typography>
          </Grid>
        )}

        <Grid container spacing={3} size={{ phone: 12, tablet: 6 }}>
          <InfoItem
            label={t("media.library.preview.fileSize")}
            value={`${NF.getDefaultFormatter().format(Number(mediaItem?.size))} MB`}
          />

          <InfoItem
            label={t("media.library.preview.fileExtension")}
            value={(mediaItem && PREVIEWS[mediaType].extension?.(mediaItem)) || ""}
          />

          {isPDF && (
            <InfoItem
              label={t("media.library.preview.pages")}
              value={mediaItem?.pageCount}
              size={{ phone: 6, tablet: 6 }}
            />
          )}

          {isImage && (
            <InfoItem
              label={t("media.library.preview.fileDimensions")}
              value={`${mediaItem?.width}x${mediaItem?.height} px`}
              size={{ phone: 6, tablet: 6 }}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
}
